<template>
  <div ref="galleryRootElement" class="h-100dvh overflow-y-auto">
    <DefaultHeader
      v-model:keyword="keyword"
      :hide-search-bar="isEventTagEnable ? false : hideHeaderSearchBar"
      use-emit-search
      @search="search"
    >
    </DefaultHeader>
    <EventBanner v-if="isEventTagEnable" />
    <ArtistsBanner v-else class="flex items-center justify-center"
      ><div ref="searchBarElement" class="w-400px flex flex-col items-center justify-center">
        <p class="mb-12px text-center title-18-h18-b text-primary-100">
          {{ $t('banner.gallery') }}
        </p>
        <AuInputSearch
          v-model:model-value="keyword"
          :class="{
            'w-400px': layoutStore.isDesktop,
            'w-280px': layoutStore.isMobile,
          }"
          :placeholder="
            layoutStore.isDesktop
              ? $t('search_bar.placeholder')
              : $t('search_bar.placeholder_mobile')
          "
          @clear="
            async () => {
              await search('')
            }
          "
          @search="search"
        />
        <div
          class="mt-4px h-12px flex flex-wrap gap-12px overflow-hidden content-10-h12-r"
          :class="{
            'w-400px ml-32px': layoutStore.isDesktop,
            'w-280px ml-24px': layoutStore.isMobile,
          }"
        >
          <span class="text-primary-90">{{ $t('banner.popular') }}</span>
          <span
            v-for="(term, index) in popularList"
            :key="index"
            class="cursor-pointer text-primary-50"
            @click="() => search(term)"
          >
            {{ term }}
          </span>
        </div>
      </div>
    </ArtistsBanner>
    <div class="mx-auto max-w-1240px flex">
      <div
        v-if="layoutStore.isDesktop"
        class="sticky top-80px w-210px flex-shrink-0 overflow-overlay overflow-x-hidden px-20px py-24px"
        :class="{
          'scrollbar-hide': !isHoveringFilterArea,
          scrollbar: isHoveringFilterArea,
        }"
        style="max-height: calc(100vh - 80px)"
        @mouseout="isHoveringFilterArea = false"
        @mouseover="isHoveringFilterArea = true"
      >
        <FilterPanel v-model:artwork-type="artworkType" />
      </div>
      <div
        class="w-full flex flex-col items-center"
        :class="{
          'py-24px': layoutStore.isDesktop,
        }"
      >
        <div v-if="layoutStore.isDesktop" class="max-w-984px w-full flex flex-col">
          <span v-if="totalCount !== null" class="content-14-h16-m mb-10px text-primary-100">{{
            `“${t(`catalog.${productStore.productState?.categoryType}.title`)}” “${artworkTypeTitle}” ${$t('search_bar.result_count', { count: totalCount })}`
          }}</span>
          <TagChipsContainer :chips-data="chipsData" class="ml-16px" />
          <div class="flex flex-wrap items-center justify-center gap-x-24px gap-y-16px">
            <AuToggleSwitch
              v-if="artworkType !== ArtworkType.StickerGroup && productStore.productState !== null"
              v-model:model-value="showDevice"
              :checked-icon="
                productTypeToggleSwitchConfig[productStore.productState.categoryType].checked
              "
              :unchecked-icon="
                productTypeToggleSwitchConfig[productStore.productState.categoryType].unchecked
              "
            />
            <DeviceDropdown class="flex-1" />
            <ProductDropdown class="flex-1" />
          </div>
        </div>
        <div v-if="layoutStore.isMobile" class="w-full">
          <ProductNavigator v-if="showDevice" />
          <div class="h-48px flex items-center justify-between px-16px">
            <div class="flex cursor-pointer text-primary-80" @click="showFilterModal = true">
              <i class="icon-content-filter mr-4px" />
              <p class="content-12-h16-r">{{ $t('btn.filter') }}</p>
              <p
                v-if="chipsData.length"
                class="ml-8px h-16px w-16px flex items-center justify-center rounded-full bg-brand-deep-yellow title-12-h16-m text-white"
              >
                {{ chipsData.length }}
              </p>
            </div>
            <AuToggleSwitch
              v-if="artworkType !== ArtworkType.StickerGroup && productStore.productState !== null"
              v-model:model-value="showDevice"
              :checked-icon="
                productTypeToggleSwitchConfig[productStore.productState.categoryType].checked
              "
              :unchecked-icon="
                productTypeToggleSwitchConfig[productStore.productState.categoryType].unchecked
              "
            />
          </div>
        </div>
        <div v-if="artworksForDittoDocumentData.length" class="grid-container w-full">
          <div v-for="item in artworksForDittoDocumentData" :key="item.id" ref="scrollItems">
            <DisplayCard :content="item" />
          </div>
        </div>
        <LoadingSpinner v-if="totalCount === null" class="mt-100px" is-loading />
        <p
          v-else-if="!artworksForDittoDocumentData.length"
          v-clean-html="$t('message.gallery-block')"
          class="mt-100px text-center content-12-h24-r text-primary-80"
        />
      </div>
    </div>
    <SideModal
      v-if="layoutStore.isMobile"
      v-model:visible="showFilterModal"
      class="h-100dvh overflow-scroll pt-20px"
    >
      <FilterPanel
        v-model:artwork-type="artworkType"
        is-mobile
        :is-single-image="artworkType === ArtworkType.StickerGroup"
      />
      <div class="sticky bottom-0 flex justify-between gap-24px bg-white py-16px">
        <AuButton class="flex-1" @click="tagStore.removeAllTags()">
          {{ $t('btn.clear') }}
        </AuButton>
        <AuButton class="flex-1" @click="showFilterModal = false">
          {{ $t('btn.filter_or_close') }}
        </AuButton>
      </div>
    </SideModal>
    <!--  Floating Action Button  -->
    <div
      class="fixed bottom-24px right-24px z-50 h-80px w-80px cursor-pointer border-1px border-primary-50 rounded-full bg-white shadow-dropdown"
      @click="
        () => {
          pushGA4ButtonClick('gallery floating action')
          $router.push(Route.DesignPage)
        }
      "
    >
      <img
        :alt="$t('btn.cta')"
        class="absolute bottom-20px max-w-none w-80px"
        src="https://assets.amuse.com.tw/products/iphone-white-sticker-45deg.webp"
      />
      <p class="absolute bottom-8px w-full text-center content-10-h16-b text-primary-80">
        {{ $t('btn.cta') }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AuButton, AuInputSearch, AuToggleSwitch } from '@evolutivelabs/amuse-ui'
import { t } from 'i18next'
import { storeToRefs } from 'pinia'
import { useQuery } from 'villus'
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue'

import { toFullSizeImage } from '@/components/designArea/utils'
import {
  type DisplayCardContent,
  type MultiLayerPreviewBackgroundDisplayCardContent,
  type MultiLayersPreviewCollageDisplayCardContent,
  type SingleImagePreviewBackgroundDisplayCardContent,
  type SingleImagePreviewCollageDisplayCardContent,
} from '@/components/DisplayCards/DisplayCard'
import DisplayCard from '@/components/DisplayCards/DisplayCard.vue'
import {
  buildBackgroundDisplayCardContent,
  buildCollageDisplayCardContent,
  buildStickerGroupDisplayCardContent,
} from '@/components/DisplayCards/DisplayCardContentBuilder'
import ArtistsBanner from '@/components/layout/ArtistsBanner.vue'
import DefaultHeader from '@/components/layout/DefaultHeader.vue'
import EventBanner from '@/components/layout/EventBanner.vue'
import FilterPanel from '@/components/layout/FilterPanel.vue'
import SideModal from '@/components/layout/SideModal.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import DeviceDropdown from '@/components/newProductPanel/desktop/DeviceDropdown.vue'
import ProductDropdown from '@/components/newProductPanel/desktop/ProductDropdown.vue'
import ProductNavigator from '@/components/productNavigator/mobile/ProductNavigator.vue'
import TagChipsContainer from '@/components/TagChipsContainer.vue'
import { useCurrentProductDisplay } from '@/data/useNewProductDisplay'
import {
  ArtworkType,
  BackgroundsForSearchDocument,
  CollagesForSearchDocument,
  StickerGroupsForSearchDocument,
} from '@/graphql/creator-platform/generated'
import { backendLocation } from '@/graphql/utils'
import { Route } from '@/router/route'
import { useEventStore } from '@/store/eventStore'
import { useLayoutStore } from '@/store/layoutStore'
import { useNewProductStore } from '@/store/newProductStore'
import { useTagStore } from '@/store/tagStore'
import { popularList } from '@/utils/common'
import { pushGA4ButtonClick } from '@/utils/googleAnalytics'
import {
  getQueryStringArtworkType,
  getQueryStringKeyword,
  setQueryStringArtworkType,
  setQueryStringKeyword,
} from '@/utils/queryString'
import { sleep } from '@/utils/sleep'
import { isNotNull, productTypeToggleSwitchConfig } from '@/utils/type.util'

const isHoveringFilterArea = ref(false)
const showDevice = ref(true)
const showFilterModal = ref(false)
const layoutStore = useLayoutStore()
const tagStore = useTagStore()
const productStore = useNewProductStore()
const keyword = ref<string>('')
const searchBarElement = useTemplateRef<HTMLElement>('searchBarElement')
const galleryRootElement = useTemplateRef<HTMLElement>('galleryRootElement')
const hideHeaderSearchBar = ref(true)
const { isEventTagEnable } = storeToRefs(useEventStore())
const isUpdatingQuery = ref(false)

const artworkTypeTitle = computed(() => {
  switch (artworkType.value) {
    case ArtworkType.Background: {
      return t('panel.function.bg')
    }
    case ArtworkType.Collage: {
      return t('panel.function.module')
    }
    case ArtworkType.StickerGroup: {
      return t('panel.function.sticker')
    }
    default: {
      return ''
    }
  }
})

const totalCount = ref<null | number>(null)

const chipsData = computed(() => {
  return Array.from(tagStore.tagList.values())
})
const artworkType = ref(getQueryStringArtworkType() ?? ArtworkType.Collage)

const stickerGroupData = ref<DisplayCardContent[]>([])
const collageData = ref<SingleImagePreviewCollageDisplayCardContent[]>([])
const backgroundData = ref<SingleImagePreviewBackgroundDisplayCardContent[]>([])

const pageInfo = ref({
  background: { endCursor: '', hasNextPage: true },
  collage: { endCursor: '', hasNextPage: true },
  stickerGroup: { endCursor: '', hasNextPage: true },
})
const scrollItems = useTemplateRef<HTMLElement[]>('scrollItems')
onMounted(async () => {
  keyword.value = getQueryStringKeyword()

  if (searchBarElement.value !== null) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          hideHeaderSearchBar.value = entry.isIntersecting
        })
      },
      { root: null, rootMargin: '-100px 0px 100px 0px' },
    )
    observer.observe(searchBarElement.value)
  }
  await Promise.all([setQueryStringArtworkType(artworkType.value), tagStore.init()])
  await updateQuery()
})

watch(artworkType, async () => {
  await setQueryStringArtworkType(artworkType.value)
  resetData()
  await updateQuery()
})

watch(
  () => productStore.productState?.selectedProduct?.productSet.boundDesignGroup.type,
  async (_, oldValue) => {
    // Mounted 時 oldValue === undefined => 不執行
    // 貼圖沒有綁 DesignGroup 不用重新執行
    if (artworkType.value === ArtworkType.StickerGroup || oldValue === undefined) {
      return
    }
    resetData()
    await updateQuery()
  },
)

watch(tagStore.tagList, async () => {
  resetData()
  await updateQuery()
})

function resetData(): void {
  totalCount.value = null
  pageInfo.value = {
    background: { endCursor: '', hasNextPage: true },
    collage: { endCursor: '', hasNextPage: true },
    stickerGroup: { endCursor: '', hasNextPage: true },
  }
  stickerGroupData.value = []
  collageData.value = []
  backgroundData.value = []
  galleryRootElement.value?.scrollTo({
    top: 0,
    behavior: 'smooth',
  })
}

const hasNextPage = computed((): boolean => {
  switch (artworkType.value) {
    case ArtworkType.Background: {
      return pageInfo.value.background.hasNextPage
    }
    case ArtworkType.Collage: {
      return pageInfo.value.collage.hasNextPage
    }
    case ArtworkType.StickerGroup: {
      return pageInfo.value.stickerGroup.hasNextPage
    }
    default: {
      return true
    }
  }
})
function registerScrollToEndObserver(): void {
  scrollToEndObserver.disconnect()
  const itemDoms = scrollItems.value ?? []
  const lastItem = itemDoms[itemDoms.length - 1]
  if (!(lastItem instanceof Element)) {
    return
  }
  scrollToEndObserver.observe(lastItem)
}
const scrollToEndObserver = new IntersectionObserver(
  (entries, observer) => {
    for (const e of entries) {
      if (!hasNextPage.value) {
        observer.unobserve(e.target)
        break
      }
      if (e.isIntersecting) {
        void updateQuery()
      }
    }
  },
  { threshold: 0 },
)

async function updateQuery(): Promise<void> {
  // todo(chili): 這邊是workaround作法, 用isUpdatingQuery阻擋狀態的話, 後觸發的正確query有可能被先觸發的錯誤query阻擋
  if (isUpdatingQuery.value) return
  isUpdatingQuery.value = true
  const artworkTagIds = [...tagStore.tagList.keys()]
  while (
    productStore.productState?.selectedProduct?.productSet.boundDesignGroup.type === undefined
  ) {
    await sleep(100)
  }
  const designGroupType = productStore.productState.selectedProduct.productSet.boundDesignGroup.type
  switch (artworkType.value) {
    case ArtworkType.Background: {
      if (!pageInfo.value.background.hasNextPage) {
        break
      }
      const { data, error } = await useQuery({
        query: BackgroundsForSearchDocument,
        variables: {
          location: backendLocation,
          designGroupType,
          cursorPaging: {
            after: pageInfo.value.background.endCursor,
            first: 10,
          },
          ...(artworkTagIds.length > 0 && { artworkTags: artworkTagIds }),
          ...(keyword.value.length > 0 && { keyword: keyword.value }),
        },
      })
      if (data.value === null || error.value !== null) {
        break
      }

      // 切換產品類型的時候會先被切到 grip 再改回 gripO
      // ( NavigationStore 會觸發 productStore 改可用選單順便塞 first product 當 selected
      //  又同時呼叫 cacheAndRecoveryState 想從 cookie 把上次資料蓋回去
      //  所以會有一瞬間打兩次後端的情況 ）
      // 雖然有 watch designGroupType 去 resetData 但因為非同步的關係
      // grip 的資料在清完資料後才回來, 所以會殘留過期的資料
      // 這邊在塞資料前再檢查一次是否是正確的 designGroupType
      // TODO(Carl): 產品管理的部分看未來有沒有機會重構成集中管理, 減少非同步造成的問題也會比較好閱讀
      const firstBackground = data.value.backgroundsForSearch.edges[0]?.node
      if (
        firstBackground !== undefined &&
        firstBackground.designGroupType !==
          productStore.productState.selectedProduct.productSet.boundDesignGroup.type
      ) {
        break
      }

      const newBackgrounds = await Promise.all(
        data.value.backgroundsForSearch.edges.map(
          async ({ node }) =>
            await buildBackgroundDisplayCardContent(node, { type: 'single-image' }),
        ),
      )
      backgroundData.value.push(...newBackgrounds.filter(isNotNull))
      const { endCursor, hasNextPage } = data.value.backgroundsForSearch.pageInfo
      pageInfo.value.background.endCursor = endCursor ?? ''
      pageInfo.value.background.hasNextPage = hasNextPage ?? true
      totalCount.value = data.value.backgroundsForSearch.totalCount
      break
    }
    case ArtworkType.Collage: {
      if (!pageInfo.value.collage.hasNextPage) {
        break
      }
      const { data, error } = await useQuery({
        query: CollagesForSearchDocument,
        variables: {
          location: backendLocation,
          designGroupType,
          cursorPaging: {
            after: pageInfo.value.collage.endCursor,
            first: 10,
          },
          ...(artworkTagIds.length > 0 && { artworkTags: artworkTagIds }),
          ...(keyword.value.length > 0 && { keyword: keyword.value }),
        },
      })
      if (data.value === null || error.value !== null) {
        break
      }

      const firstCollage = data.value.collagesForSearch.edges[0]?.node
      if (
        firstCollage !== undefined &&
        firstCollage.designGroupType !==
          productStore.productState.selectedProduct.productSet.boundDesignGroup.type
      ) {
        break
      }

      const newCollages = await Promise.all(
        data.value.collagesForSearch.edges.map(
          async ({ node }) => await buildCollageDisplayCardContent(node, { type: 'single-image' }),
        ),
      )
      collageData.value.push(...newCollages.filter(isNotNull))
      const { endCursor, hasNextPage } = data.value.collagesForSearch.pageInfo
      pageInfo.value.collage.endCursor = endCursor ?? ''
      pageInfo.value.collage.hasNextPage = hasNextPage ?? true
      totalCount.value = data.value.collagesForSearch.totalCount
      break
    }
    case ArtworkType.StickerGroup: {
      if (!pageInfo.value.stickerGroup.hasNextPage) {
        break
      }
      const { data, error } = await useQuery({
        query: StickerGroupsForSearchDocument,
        variables: {
          location: backendLocation,
          cursorPaging: {
            after: pageInfo.value.stickerGroup.endCursor,
            first: 10,
          },
          ...(artworkTagIds.length > 0 && { artworkTags: artworkTagIds }),
          ...(keyword.value.length > 0 && { keyword: keyword.value }),
        },
      })
      if (data.value === null || error.value !== null) {
        break
      }

      const newStickerGroups = await Promise.all(
        data.value.stickerGroupsForSearch.edges.map(
          async ({ node }) => await buildStickerGroupDisplayCardContent(node),
        ),
      )
      stickerGroupData.value.push(...newStickerGroups.filter(isNotNull))
      const { endCursor, hasNextPage } = data.value.stickerGroupsForSearch.pageInfo
      pageInfo.value.stickerGroup.endCursor = endCursor ?? ''
      pageInfo.value.stickerGroup.hasNextPage = hasNextPage ?? true
      totalCount.value = data.value.stickerGroupsForSearch.totalCount
      break
    }
  }

  await nextTick()
  registerScrollToEndObserver()
  isUpdatingQuery.value = false
}

const currentProductDisplay = useCurrentProductDisplay()

const artworksForDittoDocumentData = computed((): DisplayCardContent[] => {
  const display = currentProductDisplay.value

  function singleToMulti(
    content:
      | SingleImagePreviewBackgroundDisplayCardContent
      | SingleImagePreviewCollageDisplayCardContent,
  ):
    | MultiLayerPreviewBackgroundDisplayCardContent
    | MultiLayersPreviewCollageDisplayCardContent
    | null {
    return display === null
      ? null
      : {
          ...content,
          price: content.price + display.price,
          compareAtPrice: content.compareAtPrice + display.compareAtPrice,
          preview: {
            ...display,
            mask: display.previewMaskUrl,
            type: 'multi-layer' as const,
            design: toFullSizeImage(content.preview.imageUrl),
          },
        }
  }

  switch (artworkType.value) {
    case ArtworkType.Background: {
      return showDevice.value
        ? backgroundData.value.map(singleToMulti).filter(isNotNull)
        : backgroundData.value
    }
    case ArtworkType.Collage: {
      return showDevice.value
        ? collageData.value.map(singleToMulti).filter(isNotNull)
        : collageData.value
    }
    case ArtworkType.StickerGroup: {
      return stickerGroupData.value
    }
    default: {
      return []
    }
  }
})

async function search(resetKeyword: string): Promise<void> {
  keyword.value = resetKeyword
  await setQueryStringKeyword(keyword.value)
  resetData()
  await updateQuery()
}
</script>

<style scoped>
.grid-container {
  @apply grid gap-4px md:gap-8px p-5px md:p-8px justify-center;
  grid-template-columns: repeat(auto-fill, 180px);
}
@screen md {
  .grid-container {
    grid-template-columns: repeat(auto-fill, 240px);
  }
}
</style>
